import React from 'react'
import { useTranslation } from "react-i18next"
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3'
import { useTheme, makeStyles } from '@material-ui/core/styles';
import D3Tooltip from './D3Tooltip';
import { formatLocaleNumber } from 'helpers/emeaHelpers';
import Axes from './Axes'
import ResponsiveWrapper from '../charts/ResponsiveWrapper'
import { Typography, Box } from '@material-ui/core'

const useStyles = makeStyles(theme => ({
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    }
}));

const GroupedStackBarChart = ({
    data = [],
    stackGroups = {},
    height = 250,
    // parentWidth = 600,
    margins = { top: 15, right: 20, bottom: 60, left: 40 },
    showLegend = true,
    suffix = '',
    formatRequired = false,
    decimals = null,
    themeType = 'dark',
    loading,
    barColorProp = "set23",
    xKey = "formattedStartDate",
    stackedKeys = [],
    getGroupLabel = (group, d) => d[group],
    rotateLabel = false,
    showBarLabel = true,
    showXAxisLabel = true,
    ...rest

}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const theme = useTheme();
    const svgDimensions = {
        width: rest.parentWidth,
        height: height / 2 + margins.top + margins.bottom
    };
    const groupKeys = Object.keys(stackGroups);
    const themeColors = theme.palette[barColorProp];

    const colors = scaleOrdinal()
        .domain(stackedKeys)
        .range(themeColors.filter(Boolean));

    const getColor = (key) => {
        return colors(key);
    };

    const months = data.map(d => d[xKey]);
    const xScale = scaleBand()
        .domain(months)
        .range([margins.left, svgDimensions.width - margins.right])
        .padding(0.05);

    const groupWidth = xScale.bandwidth() * 0.7;
    const groupOffset = (xScale.bandwidth() - groupWidth) / 2;
    const innerScale = scaleBand()
        .domain(groupKeys)
        .range([0, groupWidth])
        .padding(0);

    // MAX VALUE (STACKED)
    const maxValue = data.length
        ? Math.max(
            ...data.map(d =>
                Math.max(
                    ...groupKeys.map(group =>
                        stackGroups[group].reduce(
                            (sum, key) => sum + (+d[key] || 0),
                            0
                        )
                    )
                )
            )
        )
        : 0;

    const yScale = scaleLinear()
        .domain([0, maxValue])
        .nice()
        .range([svgDimensions.height - margins.bottom, margins.top]);

    const NoDataFound = () => (
        <g transform={`translate(${svgDimensions.width / 2}, ${svgDimensions.height / 2})`} data-testid="no-data">
            <text textAnchor="middle" fill="white">
                {loading ? t("loading") : t("no_data_found")}
            </text>
        </g>
    );

    // STACKED BARS
    const renderBars = () => {
        const bw = innerScale ? innerScale.bandwidth() : xScale.bandwidth();
        return data.flatMap(d =>
            groupKeys.map((group, i) => {
                const month = d[xKey];
                const x =
                    xScale(month) +
                    groupOffset +
                    innerScale(group);
                const barWidth = innerScale.bandwidth();
                let cumulative = 0;
                return <>
                    {stackGroups[group].map(key => {
                        const value = +d[key] || 0;
                        const y0 = cumulative;
                        cumulative += value;
                        const y = yScale(cumulative);
                        const h = yScale(y0) - yScale(cumulative);
                        return (
                            <D3Tooltip
                                key={`${group}-${key}-${month}`}
                                placement="right"
                                interactive
                                themeType={themeType}
                                color={getColor(key)}
                                title={
                                    <div data-testid={`tooltip-${d[xKey]}-${group}-${key}`}>
                                        {`${getGroupLabel(group, d)} - ${t(key)}: ${formatLocaleNumber(value, formatRequired, decimals)}${suffix}`}
                                    </div>
                                }
                            >
                                <rect
                                    x={x}
                                    y={y}
                                    width={barWidth}
                                    height={h}
                                    fill={getColor(key)}
                                    stroke="#fff"
                                    strokeWidth={0.5}
                                    data-testid={`bar-${d[xKey]}-${group}-${key.split("_")[1]}`}
                                />
                            </D3Tooltip>
                        );
                    })}
                    {showBarLabel && <text
                        fontSize={12}
                        textAnchor="middle"
                        alignmentBaseline="middle"
                        fill={themeType === "dark" ? "white" : "black"}
                        x={x + bw / 2}
                        y={yScale(cumulative) - 5}
                    >
                        {d[rest?.stackTotal?.[group]]}
                    </text>
                    }
                </>
            })
        );
    };

    // X-AXIS BAR LABELS
    const renderBarLabels = () => {
        return data.flatMap(d =>
            groupKeys.map(group => {
                const month = d[xKey];
                const x =
                    xScale(month) +
                    groupOffset +
                    innerScale(group) +
                    innerScale.bandwidth() / 2;
                const labelProperties = rotateLabel
                    ? {
                        textAnchor: "end",
                        transform: `rotate(-45, ${x}, ${svgDimensions.height - 30})`,
                    }
                    : {};

                return (
                    showXAxisLabel && <text
                        key={`${group}-${month}`}
                        x={x}
                        y={svgDimensions.height - 30}
                        textAnchor="middle"
                        fill="white"
                        fontSize={11}
                        {...labelProperties}
                    >
                        {getGroupLabel(group, d)}
                    </text>
                );
            })
        );
    };

    return (
        <span data-testid="grouped-stack-bar-chart">

            <svg width={svgDimensions.width} height={svgDimensions.height}>
                {!data.length || maxValue === 0 || loading ? (
                    <NoDataFound />
                ) : (
                    <g>
                        {renderBars()}
                        {renderBarLabels()}
                        <Axes
                            scales={{ xScale, yScale }}
                            margins={margins}
                            svgDimensions={svgDimensions}
                            hideTickLines={true}
                        />
                    </g>
                )}
            </svg>

            {/* legend */}
            {showLegend && stackedKeys?.length && data.length && !loading ? (
                <Box display="flex" data-testid='legend-box' gridGap={8} ml={6} mt={3}>
                    {stackedKeys?.map(k => (
                        <Box key={k} className={classes.legendItem}>
                            <svg width={12} height={12}>
                                <rect width={12} height={12} fill={colors(k)} />
                            </svg>
                            <Typography data-testid={`legend-${k}`} variant="caption">{t(k)}</Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}
        </span>
    )
}

export default ResponsiveWrapper(GroupedStackBarChart);